import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import React, { useState } from 'react'
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { showToast } from 'App';
import { ToastType } from 'Contexts/Notification';

interface FormState {
  name: string;
  email: string;
  number: string;
  message: string;
}

interface ErrorState {
  name?: string;
  email?: string;
  number?: string;
  message?: string;
}

interface TouchedState {
  name?: boolean;
  email?: boolean;
  number?: boolean;
  message?: boolean;
}

const FormWrapper = styled.div`
  max-width: 400px;
  margin: 0 auto;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  // background: rgba(0,0,0,0.7);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.12);

  @media (max-width: 500px) {
    max-width: 100%;
    padding: 24px 4vw;
    border-radius: 0;
    box-shadow: none;
  }
`;
const StyledLabel = styled.label`
  color: white;
`;
const StyledInput = styled.input`
    border-radius:2px;
  color: black;
  background: white;
  border: 1px solid #fff;
`;
const StyledTextarea = styled.textarea`
    border-radius:2px;
  color: black;
  background: white;
  border: 1px solid #fff;
`;

const StyledTitle = styled.h2`
  text-align: center;
  margin-bottom: 24px;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 1px;
`;

const StyledButton = styled.button`
  background:linear-gradient(180deg, #6a1bbd 0%, #4b0082 100%);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 0;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: background 0.2s, transform 0.1s;  
  margin: 0 auto;
  display: block;
  width: 120px;
  &:hover {
    background: linear-gradient(180deg,rgb(203, 131, 255) 0%,rgb(132, 17, 255) 100%);
    transform: translateY(-2px) scale(1.03);
  }
    @media (max-width: 500px) {
   width: 170px;
  }
`;

function ContactUs() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    number: '',
    message: '',
  });
  const [errors, setErrors] = useState<ErrorState>({});
  const [touched, setTouched] = useState<TouchedState>({});

  const submitContactForm = httpsCallable(functions, "submitContactForm");  

  const validate = (values: FormState) => {
    const newErrors: ErrorState = {};
    if (!values.name.trim()) newErrors.name = 'Name is required';
    if (!values.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!values.number.trim()) {
      newErrors.number = 'Number is required';
    } else if (!/^\d{10,15}$/.test(values.number)) {
      newErrors.number = 'Number must be 10-15 digits';
    }
    if (!values.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTouched({ ...touched, [e.target.name]: true });
    setErrors(validate({ ...form, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate(form);
    setErrors(validationErrors);
    setTouched({ name: true, email: true, number: true, message: true });
    if (Object.keys(validationErrors).length === 0) {
      onSubmit(form);
    }
  };

  const onSubmit = async (data: FormState) => {
    try {
      await submitContactForm(data);      
      showToast("Your message has been sent successfully.", ToastType.SUCCESS);
      setForm({ name: '', email: '', number: '', message: '' });
      setErrors({});
      setTouched({});
    } catch (error) {
      showToast("Your message has been sent successfully.", ToastType.ERROR);
      console.log(error);
    }
  };

  return (
    <FormWrapper>
      <StyledTitle>Contact Us</StyledTitle>
      <form onSubmit={handleSubmit} noValidate style={{ width: '100%' }}>
        <div style={{ marginBottom: 16 }}>
          <StyledLabel>Name:</StyledLabel><br />
          <StyledInput
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            onBlur={handleBlur}
            style={{ width: '100%', padding: 10, fontSize: 16, marginTop: 4 }}
          />
          {touched.name && errors.name && (
            <div style={{ color: 'red', fontSize: 12 }}>{errors.name}</div>
          )}
        </div>
        <div style={{ marginBottom: 16 }}>
          <StyledLabel>Email:</StyledLabel><br />
          <StyledInput
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            style={{ width: '100%', padding: 10, fontSize: 16, marginTop: 4 }}
          />
          {touched.email && errors.email && (
            <div style={{ color: 'red', fontSize: 12 }}>{errors.email}</div>
          )}
        </div>
        <div style={{ marginBottom: 16 }}>
          <StyledLabel>Number:</StyledLabel><br />
          <StyledInput
            type="text"
            name="number"
            value={form.number}
            onChange={handleChange}
            onBlur={handleBlur}
            style={{ width: '100%', padding: 10, fontSize: 16, marginTop: 4 }}
          />
          {touched.number && errors.number && (
            <div style={{ color: 'red', fontSize: 12 }}>{errors.number}</div>
          )}
        </div>
        <div style={{ marginBottom: 24 }}>
          <StyledLabel>Message:</StyledLabel><br />
          <StyledTextarea
            name="message"
            value={form.message}
            onChange={handleChange}
            onBlur={handleBlur}
            style={{ width: '100%', padding: 10, fontSize: 16, marginTop: 4, minHeight: 80 }}
          />
          {touched.message && errors.message && (
            <div style={{ color: 'red', fontSize: 12 }}>{errors.message}</div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <StyledButton type="submit">Submit</StyledButton>
        </div>
      </form>
    </FormWrapper>
  );
}

export default ContactUs
